import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Phone, ArrowUpRight, Star, Play, Pause } from "lucide-react";
import { PRACTICE, ASSETS } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import SunDivider from "./SunDivider";

export default function Hero() {
  const { t } = useT();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (v.paused) {
        await v.play();
        setPlaying(true);
      } else {
        v.pause();
        setPlaying(false);
      }
    } catch (err) {
      setPlaying(!v.paused);
    }
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden grain"
    >
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[var(--accent)]/12 blur-2xl pointer-events-none" />
      <div className="absolute top-[60vh] -left-32 w-[360px] h-[360px] rounded-full bg-[var(--secondary-accent)]/15 blur-3xl pointer-events-none" />

      {/* Cinematic video band — FIRST */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        className="px-6 md:px-10 lg:px-16 pt-8 md:pt-12 pb-10 md:pb-14"
        data-testid="hero-video-band"
      >
        <div className="relative overflow-hidden rounded-[36px] border border-[var(--border)] bg-[var(--primary)]">
          <video
            ref={videoRef}
            data-testid="hero-video"
            className="w-full h-[320px] sm:h-[480px] md:h-[620px] object-cover"
            autoPlay
            loop
            muted={muted}
            playsInline
            poster={ASSETS.opening}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          >
            <source src={ASSETS.heroVideo} type="video/mp4" />
          </video>
          {/* gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--primary)]/75 via-[var(--primary)]/15 to-transparent pointer-events-none" />
          {/* caption */}
          <div className="absolute left-4 right-4 sm:left-12 sm:right-auto bottom-20 sm:bottom-12 text-[var(--bg)] sm:max-w-md z-10">
            <div className="font-mono-eyebrow text-[var(--accent)] text-[10px] sm:text-xs">
              Inside Smiles of Loudoun
            </div>
            <h2 className="font-serif-display text-[22px] sm:text-[32px] md:text-[48px] leading-[1.05] mt-2">
              A family atmosphere, <span className="italic text-[var(--accent)]">truly</span>.
            </h2>
          </div>
          {/* badge: live tour */}
          <div className="absolute left-4 sm:left-12 top-4 sm:top-12 z-10">
            <span className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[var(--bg)] bg-[var(--bg)]/15 backdrop-blur px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full border border-[var(--bg)]/30">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-pulse" />
              Tour our office
            </span>
          </div>
          {/* controls */}
          <div className="absolute right-4 sm:right-5 bottom-4 sm:bottom-5 flex items-center gap-2 z-10">
            <button
              data-testid="hero-video-mute"
              onClick={() => {
                const v = videoRef.current;
                if (v) {
                  v.muted = !v.muted;
                  setMuted(v.muted);
                }
              }}
              className="text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full bg-[var(--bg)]/15 text-[var(--bg)] border border-[var(--bg)]/30 hover:bg-[var(--bg)] hover:text-[var(--primary)] backdrop-blur"
              aria-label={muted ? "Unmute video" : "Mute video"}
            >
              {muted ? t("Unmute") : t("Mute")}
            </button>
            <button
              data-testid="hero-video-toggle"
              onClick={togglePlay}
              className="w-11 h-11 rounded-full bg-[var(--accent)] text-[var(--primary)] grid place-content-center hover:bg-[var(--accent-hover)]"
              aria-label={playing ? "Pause video" : "Play video"}
            >
              {playing ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Discover your smile — BELOW the video */}
      <div className="px-6 md:px-10 lg:px-16 pb-20 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
            className="font-mono-eyebrow text-[var(--text-muted)] flex items-center gap-3"
          >
            <span className="w-8 h-px bg-[var(--accent)]" />
            {t("We accept all PPO · Virginia · West Virginia Medicaid")}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-serif-display text-[var(--primary)] mt-6 text-[56px] sm:text-[72px] md:text-[86px] lg:text-[104px] leading-[0.95] tracking-tight"
          >
            {t("Discover")}<br />
            <span className="italic text-[var(--accent)]">{t("your")}</span> {t("smile.")}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-7 max-w-xl text-[17px] md:text-[18px] leading-relaxed text-[var(--text-muted)]"
          >
            {t("One-on-one dentistry in a family atmosphere. Drs. Yopa, Ide Noumy and Ebrahimi take the time you need — and tailor every treatment to the smile in front of them.")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <a
              data-testid="hero-appointment-btn"
              href={PRACTICE.appointmentUrl}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--primary)] text-[var(--bg)] hover:bg-[var(--primary-dark)]"
            >
              {t("Request Appointment")}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              data-testid="hero-call-btn"
              href={PRACTICE.phoneHref}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)]"
            >
              <Phone className="w-4 h-4" strokeWidth={1.8} />
              {PRACTICE.phone}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-10 flex flex-wrap items-center gap-6 text-sm text-[var(--text-muted)]"
          >
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-4 h-4 fill-[var(--accent)] text-[var(--accent)]"
                />
              ))}
              <span className="ml-2 text-[var(--primary)] font-medium">
                {t("Loved by Loudoun families")}
              </span>
            </div>
            <span className="hidden sm:block w-px h-5 bg-[var(--border)]" />
            <span data-testid="languages-badge">
              {t("Multilingual care · EN · FR · فارسی · ES")}
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative">
            <div className="arch-shape aspect-[3/4] w-full max-w-[440px] mx-auto bg-[var(--surface)]">
              <img
                src={ASSETS.opening}
                alt="Happy mother and daughter at Smiles of Loudoun"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 md:-left-10 bg-[var(--bg)] border border-[var(--border)] rounded-3xl px-5 py-4 shadow-[0_8px_30px_rgba(14,26,58,0.10)] flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[var(--primary)] grid place-content-center">
                <svg width="22" height="14" viewBox="0 0 22 14" fill="none">
                  <path
                    d="M1 2 C 5 13, 17 13, 21 2"
                    stroke="var(--accent)"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </div>
              <div className="leading-tight">
                <div className="font-serif-display text-[20px] text-[var(--primary)]">
                  $129
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)]">
                  {t("Exam · X-Rays · Cleaning")}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <SunDivider className="mb-2" />
    </section>
  );
}
